import React, { useReducer, useRef, useCallback, useEffect, useState } from 'react'
import { IconButton, Tooltip, Box, Typography } from '@mui/material'
import {
    Mic as MicIcon,
    Stop as StopIcon,
    HourglassEmpty as HourglassIcon,
} from '@mui/icons-material'
import { voiceReducer } from '../state/voiceReducer'
import {
    buildTranscribeRequest,
    csrfHeaders,
    friendlyTranscriptionError,
} from '../utils/transcribeApi'

const MIME_FALLBACKS = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
]

const MAX_DURATION_SEC = 120

function negotiateMime(): string | undefined {
    if (typeof MediaRecorder === 'undefined') return undefined
    for (const mime of MIME_FALLBACKS) {
        if (MediaRecorder.isTypeSupported(mime)) return mime
    }
    return undefined
}

function isMediaSupported(): boolean {
    return !!(
        typeof navigator !== 'undefined' &&
        navigator.mediaDevices &&
        typeof navigator.mediaDevices.getUserMedia === 'function' &&
        typeof window !== 'undefined' &&
        window.isSecureContext !== false
    )
}

interface VoiceButtonProps {
    onTranscript: (text: string) => void
    transcribeUrl: string
    disabled?: boolean
}

export const VoiceButton: React.FC<VoiceButtonProps> = ({
    onTranscript,
    transcribeUrl,
    disabled = false,
}) => {
    const supported = isMediaSupported()
    const resolvedMime = useRef<string | undefined>(
        supported ? negotiateMime() : undefined,
    )
    const generationRef = useRef(0)
    const onTranscriptRef = useRef(onTranscript)
    onTranscriptRef.current = onTranscript

    const [vs, dispatch] = useReducer(voiceReducer, {
        state: supported ? 'idle' : 'unsupported',
        error: null,
        elapsedSec: 0,
    })

    const mediaRecorderRef = useRef<MediaRecorder | null>(null)
    const streamRef = useRef<MediaStream | null>(null)
    const chunksRef = useRef<Blob[]>([])
    const abortRef = useRef<AbortController | null>(null)
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const [elapsed, setElapsed] = useState(0)

    const errorDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    useEffect(() => {
        return () => {
            streamRef.current?.getTracks().forEach((t) => t.stop())
            abortRef.current?.abort()
            if (timerRef.current) clearInterval(timerRef.current)
            if (errorDismissRef.current) clearTimeout(errorDismissRef.current)
            dispatch({ type: 'RESET' })
        }
    }, [])

    useEffect(() => {
        if (errorDismissRef.current) {
            clearTimeout(errorDismissRef.current)
            errorDismissRef.current = null
        }
        const isErrorState =
            vs.state === 'error' ||
            vs.state === 'denied' ||
            vs.state === 'unavailable'
        if (isErrorState) {
            errorDismissRef.current = setTimeout(() => {
                dispatch({ type: 'RESET' })
            }, 4000)
        }
        return () => {
            if (errorDismissRef.current) clearTimeout(errorDismissRef.current)
        }
    }, [vs.state])

    useEffect(() => {
        if (vs.state === 'recording') {
            setElapsed(0)
            timerRef.current = setInterval(() => {
                setElapsed((prev) => {
                    const next = prev + 1
                    if (next >= MAX_DURATION_SEC) {
                        dispatch({ type: 'STOP_RECORDING' })
                        mediaRecorderRef.current?.stop()
                    }
                    return next
                })
            }, 1000)
        } else {
            if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
            }
            if (vs.state !== 'stopping' && vs.state !== 'uploading') setElapsed(0)
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current)
        }
    }, [vs.state])

    const startRecording = useCallback(async () => {
        dispatch({ type: 'REQUEST_PERMISSION' })

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
            streamRef.current = stream

            const mimeOpt = resolvedMime.current
                ? { mimeType: resolvedMime.current }
                : undefined
            const recorder = new MediaRecorder(stream, mimeOpt)
            chunksRef.current = []
            const gen = ++generationRef.current

            recorder.ondataavailable = (e: BlobEvent) => {
                if (e.data.size > 0) chunksRef.current.push(e.data)
            }

            recorder.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop())
                streamRef.current = null
                dispatch({ type: 'CHUNKS_READY' })

                const mimeType = resolvedMime.current || 'audio/webm'
                const audioBlob = new Blob(chunksRef.current, { type: mimeType })
                const langHint = (navigator.language || '').split('-')[0] || undefined
                const formData = buildTranscribeRequest(audioBlob, langHint)

                const controller = new AbortController()
                abortRef.current = controller

                try {
                    const response = await fetch(transcribeUrl, {
                        method: 'POST',
                        headers: csrfHeaders(),
                        body: formData,
                        credentials: 'include',
                        signal: controller.signal,
                    })

                    if (gen !== generationRef.current) return

                    if (!response.ok) {
                        const body = await response.json().catch(() => ({}))
                        dispatch({
                            type: 'UPLOAD_FAILURE',
                            error: friendlyTranscriptionError(
                                response.status,
                                body.detail as string | undefined,
                            ),
                        })
                        return
                    }

                    const data = await response.json()
                    const text = (data.text as string) || ''
                    if (text.trim()) onTranscriptRef.current(text)
                    dispatch({ type: 'UPLOAD_SUCCESS' })
                } catch (err: unknown) {
                    if (
                        err instanceof Error &&
                        err.name === 'AbortError'
                    ) {
                        return
                    }
                    if (gen !== generationRef.current) return
                    dispatch({
                        type: 'UPLOAD_FAILURE',
                        error:
                            err instanceof Error
                                ? err.message
                                : 'Transcription failed',
                    })
                }
            }

            recorder.start()
            mediaRecorderRef.current = recorder
            dispatch({ type: 'PERMISSION_GRANTED' })
        } catch (err: unknown) {
            if (err instanceof Error && err.name === 'NotAllowedError') {
                dispatch({ type: 'PERMISSION_DENIED' })
            } else {
                dispatch({ type: 'PERMISSION_UNAVAILABLE' })
            }
        }
    }, [transcribeUrl])

    const handleClick = useCallback(() => {
        if (disabled || vs.state === 'unsupported') return

        if (vs.state === 'recording') {
            dispatch({ type: 'STOP_RECORDING' })
            mediaRecorderRef.current?.stop()
        } else if (
            vs.state === 'error' ||
            vs.state === 'denied' ||
            vs.state === 'unavailable' ||
            vs.state === 'idle'
        ) {
            startRecording()
        }
    }, [vs.state, disabled, startRecording])

    const isRecording = vs.state === 'recording'
    const isProcessing =
        vs.state === 'stopping' ||
        vs.state === 'uploading' ||
        vs.state === 'awaiting_permission'
    const isUnsupported = vs.state === 'unsupported'
    const hasError =
        vs.state === 'error' || vs.state === 'denied' || vs.state === 'unavailable'

    const formatTime = (sec: number) => {
        const m = Math.floor(sec / 60).toString()
        const s = (sec % 60).toString().padStart(2, '0')
        return `${m}:${s}`
    }

    let tooltip = 'Start voice input'
    if (isRecording) tooltip = `Stop recording (${formatTime(elapsed)})`
    if (vs.state === 'awaiting_permission') tooltip = 'Requesting microphone…'
    if (vs.state === 'stopping' || vs.state === 'uploading') tooltip = 'Transcribing…'
    if (vs.state === 'denied') tooltip = 'Microphone blocked — click to retry'
    if (vs.state === 'error') tooltip = vs.error || 'Voice input error — click to retry'
    if (vs.state === 'unavailable') tooltip = 'No microphone found — click to retry'
    if (isUnsupported) tooltip = 'Voice input requires a secure connection (HTTPS)'

    const icon = isProcessing ? (
        <HourglassIcon sx={{ fontSize: 20 }} />
    ) : isRecording ? (
        <StopIcon sx={{ fontSize: 20 }} />
    ) : (
        <MicIcon sx={{ fontSize: 20 }} />
    )

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Tooltip title={tooltip} placement="top" slotProps={{ popper: { style: { zIndex: 100000 } } }}>
                <span>
                    <IconButton
                        onClick={handleClick}
                        disabled={disabled || isProcessing || isUnsupported}
                        aria-label={tooltip}
                        size="small"
                        sx={{
                            color: isRecording
                                ? 'var(--error-color, #ef4444)'
                                : hasError
                                  ? 'var(--warning-color, #f59e0b)'
                                  : 'var(--main-text-secondary, #64748b)',
                            ...(isRecording && {
                                animation: 'voice-pulse 1.5s ease-in-out infinite',
                                '@keyframes voice-pulse': {
                                    '0%, 100%': { opacity: 1 },
                                    '50%': { opacity: 0.65 },
                                },
                            }),
                        }}
                    >
                        {icon}
                    </IconButton>
                </span>
            </Tooltip>
            {hasError && vs.error && (
                <Typography
                    role="alert"
                    sx={{
                        fontSize: 10,
                        color: 'var(--warning-color, #f59e0b)',
                        maxWidth: 120,
                        textAlign: 'center',
                        lineHeight: 1.3,
                        mt: 0.25,
                    }}
                >
                    {vs.error}
                </Typography>
            )}
        </Box>
    )
}

export default VoiceButton
